import { CartProductDomainPromo } from '../../../core/models/cart.model';
import { CreateOrderProduct } from '../../../core/models/order.model';
import { CartItem } from '../../../core/store/cart/cart.state';
import {
  DEFAULT_DOMAIN_REGISTRATION_ICON,
  EMAIL_DOMAIN_FIELD_NAME,
} from '../../../shared/constants/domain-promo.constants';
import {
  FREE_DOMAIN_INCLUDED_UUIDS,
  REGISTRY_DOMAIN_UUIDS,
} from '../../../shared/constants/variation-uuids.constant';
import { isEmailEmpresarialVariationUuid } from '../../../core/utils/cart-domain-suggestion-rules';

interface CartRequiredField { name: string; value?: string }

/** Cupom que habilita registro de domínio grátis (regra de negócio). */
export const PROMO_HUB_PMC_COUPON = 'PROMOHUBPMC';

/** Valor do registro de domínio pago quando o validate-order não sinaliza gratuidade. */
export const HOSTING_SITE_REGISTRATION_PRICE = 55.00;

const PRESENCA_DOMAIN_FIELD_NAMES = ['com_br_domain', 'com_domain', 'net_domain'] as const;
const DOMAIN_FIELD_NAMES = [
  ...PRESENCA_DOMAIN_FIELD_NAMES,
  'google_ws_domain',
  EMAIL_DOMAIN_FIELD_NAME,
] as const;

function isRegistryDomainLineUuid(lineUuid: string): boolean {
  const u = String(lineUuid ?? '').trim().toLowerCase();
  if (!u) return false;
  return REGISTRY_DOMAIN_UUIDS.some(x => String(x).trim().toLowerCase() === u);
}

function isFreeDomainIncludedVariationUuid(variationUuid: string | undefined | null): boolean {
  const u = String(variationUuid ?? '').trim().toLowerCase();
  if (!u) return false;
  return FREE_DOMAIN_INCLUDED_UUIDS.some(x => String(x).trim().toLowerCase() === u);
}

export function isFreeDomainIncludedCartItem(item: CartItem): boolean {
  return isFreeDomainIncludedVariationUuid(item.selectedVariation?.uuid);
}

function collectRequiredFields(item: CartItem): CartRequiredField[] {
  return [
    ...(item.selectedVariation?.requiredFields ?? []),
    ...(item.product?.requiredFields ?? []),
  ];
}

function getFieldValue(
  fields: CartRequiredField[],
  fieldName: string
): string | undefined {
  const raw = fields.find(f => f.name === fieldName)?.value;
  const v = typeof raw === 'string' ? raw.trim() : '';
  return v.length ? v : undefined;
}

export function isEmailEmpresarialCartItem(item: CartItem): boolean {
  return isEmailEmpresarialVariationUuid(item.selectedVariation?.uuid);
}

export function isGoogleWorkspaceCartItem(item: CartItem): boolean {
  return collectRequiredFields(item).some(f => f.name === 'google_ws_domain');
}

function isAlwaysPaidNewDomainCartItem(item: CartItem): boolean {
  return isGoogleWorkspaceCartItem(item) || isEmailEmpresarialCartItem(item);
}

export function getRegistrationDomainFromCartItem(item: CartItem): string | null {
  const all = getAllRegistrationDomainsFromCartItem(item);
  return all[0] ?? null;
}

export function getAllRegistrationDomainsFromCartItem(item: CartItem): string[] {
  const fields = collectRequiredFields(item);
  const domains: string[] = [];
  for (const f of fields) {
    if (
      (f.name === 'com_br_domain' ||
        f.name === 'com_domain' ||
        f.name === 'net_domain' ||
        f.name === 'google_ws_domain' ||
        f.name === EMAIL_DOMAIN_FIELD_NAME) &&
      typeof f.value === 'string'
    ) {
      const v = f.value.trim();
      if (v && v.includes('.')) {
        domains.push(v);
      }
    }
  }
  return domains;
}

export function getDomainPreferenceFromCartItem(
  item: CartItem
): 'new' | 'existing' | null {
  const fields = collectRequiredFields(item);
  const v = getFieldValue(fields, 'domain_preference')?.toLowerCase();
  if (v === 'new' || v === 'existing') {
    return v;
  }
  return null;
}

function getDomainFromOrderRequiredFields(
  fields: { name: string; value?: string }[] | undefined
): string | null {
  if (!fields?.length) {
    return null;
  }
  for (const name of DOMAIN_FIELD_NAMES) {
    const raw = fields.find(f => f.name === name)?.value;
    const v = typeof raw === 'string' ? raw.trim() : '';
    if (v && v.includes('.')) {
      return v;
    }
  }
  return null;
}

function getDomainPreferenceFromOrderRequiredFields(
  fields: { name: string; value?: string }[] | undefined
): 'new' | 'existing' | null {
  if (!fields?.length) {
    return null;
  }
  const raw = fields.find(f => f.name === 'domain_preference')?.value;
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (v === 'new' || v === 'existing') {
    return v;
  }
  return null;
}

function resolveRegistryDomainFromValidatedProducts(
  domainName: string,
  validatedProducts: CreateOrderProduct[] | null | undefined
): { isFree: boolean } | null {
  if (!validatedProducts?.length || !domainName) {
    return null;
  }

  const normalizedDomain = domainName.trim().toLowerCase();

  for (const p of validatedProducts) {
    const pUuid = String(p.uuid ?? '');

    // Só consideramos linhas de registro de domínio.
    if (!isRegistryDomainLineUuid(pUuid)) {
      continue;
    }

    const preference = getDomainPreferenceFromOrderRequiredFields(p.requiredFields);
    // Se o validate-order sinalizar domínio "existing", não exibir domain-promo.
    if (preference && preference !== 'new') {
      return null;
    }

    const d = getDomainFromOrderRequiredFields(p.requiredFields)?.toLowerCase();
    if (!d || d !== normalizedDomain) {
      continue;
    }

    // Alguns ambientes retornam `free: true` mesmo com cupom vazio.
    if ((p as CreateOrderProduct & { free?: boolean })?.free === true) {
      return { isFree: true };
    }

    const c = p.coupon;
    const cup = typeof c === 'string' ? c.trim().toUpperCase() : '';
    if (cup === PROMO_HUB_PMC_COUPON) {
      return { isFree: true };
    }

    // Linha registry sem cupom → pago
    return { isFree: false };
  }

  return null;
}

/**
 * No validate-order, linhas extras de registro trazem o mesmo domínio:
 * - `coupon === null` ou `""` → pago (valor fixo `HOSTING_SITE_REGISTRATION_PRICE`)
 * - `coupon === PROMOHUBPMC` → grátis
 *
 * Google e Email Empresarial nunca são grátis — use `isAlwaysPaidNewDomainCartItem` nos builders de promo.
 */
export function resolveDomainRegistrationFromValidatedProducts(
  domainName: string,
  validatedProducts: CreateOrderProduct[] | null | undefined
): { isFree: boolean } | null {
  return resolveRegistryDomainFromValidatedProducts(domainName, validatedProducts);
}

export interface BuildCartDomainPromoOptions {
  validatedProducts: CreateOrderProduct[] | null | undefined;
  hideDomainPromosUntilValidated?: boolean;
}

function buildPaidDomainPromo(domainName: string): CartProductDomainPromo {
  return {
    domainName,
    isFree: false,
    price: HOSTING_SITE_REGISTRATION_PRICE,
    icon: DEFAULT_DOMAIN_REGISTRATION_ICON,
  };
}

function hasMatchingRegistryLineForDomain(
  domainName: string,
  validatedProducts: CreateOrderProduct[] | null | undefined
): boolean {
  return resolveRegistryDomainFromValidatedProducts(domainName, validatedProducts) !== null;
}

export function buildCartDomainPromoForCartItem(
  row: CartItem,
  _productName: string,
  options: BuildCartDomainPromoOptions
): CartProductDomainPromo | null {
  if (
    options.hideDomainPromosUntilValidated &&
    !options.validatedProducts?.length
  ) {
    return null;
  }

  const domain = getRegistrationDomainFromCartItem(row);
  if (!domain) {
    return null;
  }

  const preference = getDomainPreferenceFromCartItem(row);
  if (preference !== 'new') {
    return null;
  }

  // Google e Email Empresarial: sempre pago (ignora free/cupom do validate-order).
  if (isAlwaysPaidNewDomainCartItem(row)) {
    if (!hasMatchingRegistryLineForDomain(domain, options.validatedProducts)) {
      return null;
    }
    return buildPaidDomainPromo(domain);
  }

  if (!isFreeDomainIncludedCartItem(row)) {
    return null;
  }

  const resolved = resolveDomainRegistrationFromValidatedProducts(domain, options.validatedProducts);
  if (!resolved) {
    return null;
  }

  const isFree = resolved.isFree;
  return {
    domainName: domain,
    isFree,
    price: isFree ? 0 : HOSTING_SITE_REGISTRATION_PRICE,
    icon: DEFAULT_DOMAIN_REGISTRATION_ICON,
  };
}

export function buildCartDomainPromosForCartItem(
  row: CartItem,
  _productName: string,
  options: BuildCartDomainPromoOptions
): CartProductDomainPromo[] {
  const qty = Math.max(1, row.quantity ?? 1);

  const cartDomains = getAllRegistrationDomainsFromCartItem(row);

  // Se ainda não validou e não tem domínio no carrinho, não há o que exibir.
  if (!cartDomains.length) {
    return [];
  }

  // Respeita a regra de "só aparecer depois do validate-order", quando configurada.
  if (options.hideDomainPromosUntilValidated && !options.validatedProducts?.length) {
    return [];
  }

  const isAlwaysPaid = isAlwaysPaidNewDomainCartItem(row);
  if (!isAlwaysPaid && !isFreeDomainIncludedCartItem(row)) {
    return [];
  }

  if (isAlwaysPaid) {
    const preference = getDomainPreferenceFromCartItem(row);
    if (preference !== 'new') {
      return [];
    }
  }

  const promos: CartProductDomainPromo[] = [];
  // Regra:
  // - antes do validate-order: não replica promo ao aumentar quantity
  // - depois do validate-order: exibe promo(s) conforme os domínios "new" retornados
  const renderCount = Math.min(qty, cartDomains.length);
  for (let i = 0; i < renderCount; i++) {
    const domainName = cartDomains[i] ?? '';
    if (!domainName) continue;

    if (isAlwaysPaid) {
      if (!hasMatchingRegistryLineForDomain(domainName, options.validatedProducts)) {
        continue;
      }
      promos.push(buildPaidDomainPromo(domainName));
      continue;
    }

    const resolved = resolveRegistryDomainFromValidatedProducts(domainName, options.validatedProducts);
    if (!resolved) {
      continue;
    }

    const isFree = resolved.isFree;
    promos.push({
      domainName,
      isFree,
      price: isFree ? 0 : HOSTING_SITE_REGISTRATION_PRICE,
      icon: DEFAULT_DOMAIN_REGISTRATION_ICON,
    });
  }

  return promos;
}

/** Primeira linha do produto com domínio a exibir (legado; preferir por variação em `buildCartDomainPromoForCartItem`). */
export function buildCartDomainPromoForProduct(
  productName: string,
  rows: CartItem[],
  options: BuildCartDomainPromoOptions
): CartProductDomainPromo | null {
  for (const row of rows) {
    const promo = buildCartDomainPromoForCartItem(row, productName, options);
    if (promo) {
      return promo;
    }
  }
  return null;
}
